import React, { useState, useEffect, useCallback } from 'react';
import { 
  MoreHorizontal, Plus, GripVertical, 
  Instagram, Facebook, Linkedin, Youtube,
  MessageSquare, Calendar, User, Phone,
  Users, TrendingUp, CheckCircle2, ArrowUpRight,
  Search, SlidersHorizontal, Download, Mail, Edit, Trash2
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import LeadEditModal from './LeadEditModal';
import { Badge } from '@/ui/badge';
import { Button } from '@/ui/button';
import { Card, CardContent } from '@/ui/card';
import { ScrollArea } from '@/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/avatar";
import { Input } from "@/ui/input";
import { cn } from "@/ui/utils";
import API from '@/api/api01';
import { toast } from 'sonner';
import { exportToCSV } from '@/utils/exportUtils';

const STAGES = [
  { id: 'New', title: 'New Leads', color: 'bg-blue-500' },
  { id: 'Verified', title: 'Verified', color: 'bg-emerald-500' },
  { id: 'Contacted', title: 'Contacted', color: 'bg-amber-500' },
  { id: 'Qualified', title: 'Qualified', color: 'bg-indigo-500' },
  { id: 'Converted', title: 'Converted', color: 'bg-purple-500' },
];

export default function SocialKanban() {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingLead, setEditingLead] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all leads for Kanban (verified_only=None)
      const res = await API.get('/superadmin/social-leads/all');
      setLeads(res.data);
      if (res.data.length > 0) {
        toast.success("Kanban board updated");
      } else {
        toast.error("No records found");
      }
    } catch (err) {
      toast.error("Failed to load Kanban data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleDragStart = (lead: any) => {
    setDraggedLead(lead);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (stageId: string) => {
    if (!draggedLead) return;
    if (draggedLead.status === stageId) {
      setDraggedLead(null);
      return;
    }

    const originalLeads = [...leads];
    // Optimistic update
    setLeads((prev: any[]) => prev.map((l: any) => (l.id === draggedLead.id ? { ...l, status: stageId } : l)));

    try {
      await API.put(`/superadmin/social-leads/${draggedLead.id}/status`, { status: stageId });
      toast.success(`Moved ${draggedLead.name} to ${stageId}`);
    } catch (err) {
      setLeads(originalLeads);
      toast.error("Failed to update status");
    } finally {
      setDraggedLead(null);
    }
  };

  const getLeadsByStage = (stage: string) => {
    return leads.filter((l: any) => 
      l.status === stage && 
      (l.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
       (l.email && l.email.toLowerCase().includes(searchQuery.toLowerCase())))
    );
  };

  const handleCommClick = (type: string, name: string) => {
    toast.info(`Initiating ${type} with ${name}...`, {
      description: "Direct integration coming soon."
    });
  };

  const getPriorityInfo = (score: number) => {
    if (score >= 80) return { label: "High", color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400" };
    if (score >= 50) return { label: "Medium", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400" };
    return { label: "Low", color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400" };
  };

  const stats = {
    total: leads.length,
    active: leads.filter((l: any) => l.status !== 'Converted').length,
    verified: leads.filter((l: any) => l.is_verified).length,
    conversion: leads.length > 0 ? Math.round((leads.filter((l: any) => l.status === 'Converted').length / leads.length) * 100) : 0
  };

  const handleExport = () => {
    if (leads.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = leads.map((l: any) => ({
      ID: l.id,
      Name: l.name,
      Email: l.email || 'N/A',
      Phone: l.phone || 'N/A',
      Platform: l.platform,
      Campaign: l.campaign_name || 'N/A',
      Status: l.status,
      Score: l.lead_score,
      Source: l.lead_source || 'N/A',
      Verified: l.is_verified ? 'Yes' : 'No',
      CreatedAt: new Date(l.created_at).toLocaleDateString()
    }));

    exportToCSV(exportData, `social_leads_kanban_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Kanban leads exported successfully");
  };

  const handleDelete = async (leadId: string | number) => {
    if (!confirm("Delete this lead?")) return;
    try {
      setLeads((prev: any[]) => prev.filter((l: any) => l.id !== leadId));
      await API.delete(`/superadmin/social-leads/${leadId}`);
      toast.success("Lead record synchronized and removed");
    } catch (err) {
      toast.error("Failed to delete record");
      fetchLeads();
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-background">
      {/* Top Stats Bar - Consistent with LMS */}
      <div className="bg-white dark:bg-card border-b border-slate-200 dark:border-slate-800 px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">{stats.total}</p>
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60 mt-1">Total Leads</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">{stats.active}</p>
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60 mt-1">Active Leads</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">{stats.verified}</p>
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60 mt-1">Verified</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
              <ArrowUpRight className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">{stats.conversion}%</p>
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60 mt-1">Conversion</p>
            </div>
          </div>
        </div>
      </div>


      {/* Action Bar */}
      <div className="bg-white dark:bg-card border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search social leads..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-lg bg-slate-50 dark:bg-background border-slate-200 dark:border-slate-800"
          />
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
          <Button variant="outline" onClick={handleExport} className="rounded-lg whitespace-nowrap">
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
          <Button variant="outline" size="icon" className="rounded-lg">
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="h-full p-6">
          <div className="flex gap-4 h-full min-w-max">
            {STAGES.map((stage) => {
              const stageLeads = getLeadsByStage(stage.id);
              return (
                <div
                  key={stage.id}
                  className="flex-shrink-0 w-80 flex flex-col"
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(stage.id)}
                >
                  {/* Stage Header - LMS Inspired */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={cn("h-2 w-2 rounded-full", stage.color)} />
                        <h3 className="font-semibold text-slate-900 dark:text-white uppercase text-xs tracking-wider">
                          {stage.title}
                        </h3>
                      </div>
                      <Badge className={cn("rounded-full text-white px-2 py-0 h-5", stage.color)}>
                        {stageLeads.length}
                      </Badge>
                    </div>
                    <div className="h-1 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className={cn("h-full", stage.color)} style={{ width: "100%" }} />
                    </div>
                  </div>

                  {/* Column Body */}
                    <div className="flex-1 overflow-y-auto space-y-3 pb-4 scrollbar-hide">
                      {stageLeads.length === 0 ? (
                        <div className="h-32 flex flex-col items-center justify-center text-slate-400 dark:text-slate-600 text-[10px] border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/10">
                          <Users className="h-6 w-6 mb-1 opacity-20" />
                          <p>No records found</p>
                          <span className="mt-1 opacity-50">Drop lead here</span>
                        </div>
                      ) : (
                      stageLeads.map((lead: any) => {
                        const prio = getPriorityInfo(lead.lead_score);
                        return (
                          <Card
                            key={lead.id}
                            draggable
                            onDragStart={() => handleDragStart(lead)}
                            className="group cursor-move hover:shadow-lg transition-all duration-200 bg-white dark:bg-card border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden"
                          >
                            <CardContent className="p-4 space-y-3">
                              {/* Card Header */}
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="h-5 w-5 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                    {lead.platform === 'Instagram' && <Instagram size={12} className="text-[#E1306C]" />}
                                    {lead.platform === 'Facebook' && <Facebook size={12} className="text-[#1877F2]" />}
                                    {lead.platform === 'LinkedIn' && <Linkedin size={12} className="text-[#0A66C2]" />}
                                    {lead.platform === 'YouTube' && <Youtube size={12} className="text-[#FF0000]" />}
                                  </div>
                                  <span className="text-[10px] font-bold text-slate-400 tracking-tighter uppercase">{lead.platform}</span>
                                </div>
                                <Badge className={cn("text-[10px] h-5 px-1.5 shadow-none border-none", prio.color)}>
                                  {prio.label}
                                </Badge>
                              </div>

                              <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm">
                                  {lead.name}
                                </h4>
                                <p className="text-[11px] text-blue-600 dark:text-blue-400 font-medium">
                                  {lead.campaign_name || "Direct Organic"}
                                </p>
                              </div>

                              {/* Contact Info */}
                              <div className="space-y-1.5 pt-1">
                                {lead.phone && (
                                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                    <Phone className="h-3 w-3" />
                                    <span className="text-[11px]">{lead.phone}</span>
                                  </div>
                                )}
                                {lead.email && (
                                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                    <Mail className="h-3 w-3" />
                                    <span className="text-[11px] truncate">{lead.email}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                  <Activity size={12} className="text-purple-500" />
                                  <span className="text-[11px]">Score: {lead.lead_score}</span>
                                </div>
                              </div>

                              {/* Footer */}
                              <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex -space-x-1.5">
                                  <Avatar className="h-6 w-6 border-2 border-white dark:border-slate-900">
                                    <AvatarFallback className="bg-purple-100 text-purple-600 text-[10px] font-bold">
                                      {lead.name[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleCommClick('Message', lead.name); }}
                                    className="h-8 w-8 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                                  >
                                      <MessageSquare size={14} />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleCommClick('Phone', lead.name); }}
                                    className="h-8 w-8 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                  >
                                      <Phone size={14} />
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                                        <MoreHorizontal size={14} />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48 rounded-xl shadow-xl border-white/10 bg-card/95 backdrop-blur-xl">
                                      <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Lead Actions</DropdownMenuLabel>
                                      <DropdownMenuItem 
                                        className="gap-2 focus:bg-blue-500/10 focus:text-blue-500 cursor-pointer rounded-lg mx-1"
                                        onClick={() => { setEditingLead(lead); setIsEditModalOpen(true); }}
                                      >
                                        <Edit size={14} /> Edit Lead Details
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator className="bg-white/5" />
                                      <DropdownMenuItem 
                                        className="gap-2 focus:bg-red-500/10 focus:text-red-500 cursor-pointer rounded-lg mx-1"
                                        onClick={() => handleDelete(lead.id)}
                                      >
                                        <Trash2 size={14} /> Delete Record
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <LeadEditModal 
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        lead={editingLead}
        type="social"
        onSuccess={fetchLeads}
      />
    </div>
  );
}

const Activity = ({ size, className }: any) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
);
